import { DashboardType } from '@/core/domain/workorder/Dashboard/Dashboard.types';
import { useDisclosure, useWindowSize } from '@/hooks';
import { Col, Row } from 'antd';
import moment from 'moment';
import React from 'react';
import StatisticDashboard, {
  StatisticDashboardModule,
  StatisticDashboardTypeColor,
} from '@/components/_common/StatisticDashboard';
import AccountReceivableListModal from '@/components/_common/AccountReceivableListModal';
import { AccountReceivableSituation } from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { DateTimeFormat } from '@/utils/moment';
import AccountPayableListModal from '@/components/_common/AccountPayableListModal';
import { InstallmentSituation } from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import WorkOrdersListModal from '@/components/_common/WorkOrdersListModal';
import { WorkOrderComplementStatusNumber } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import formatterPrice from '@/utils/number/formatterPrice';
import { useDashboardQuick } from '@/core/domain/workorder/Dashboard/Dashboard.hooks';
import SkeletonCustom from '@/components/_common/SkeletonCustom';

const StatisticManagerView: React.FC = () => {
  const { isMobile } = useWindowSize();

  const [viewAccountReceivablesOpen, viewAccountReceivablesActions] =
    useDisclosure();
  const [viewAccountPayablesOpen, viewAccountPayablesActions] = useDisclosure();
  const [viewWorkOrdersEntryOpen, viewWorkOrdersEntryActions] = useDisclosure();
  const [viewWorkOrdersAuthorizationOpen, viewWorkOrdersAuthorizationActions] =
    useDisclosure();

  const { data, isLoading } = useDashboardQuick({
    dashboardType: DashboardType.management,
  });

  return (
    <SkeletonCustom loading={isLoading} skeletonType="INPUT" skeletonCount={4}>
      <Row gutter={[24, 24]} justify="space-between">
        <Col>
          <StatisticDashboard
            tooltipTitle="Todos os lançamentos do contas a receber com data de vencimento do mês atual."
            statisticTitle={`A Receber | ${moment()
              .format('MMM')
              .toUpperCase()}`}
            statisticValue={formatterPrice(data?.receivableMonthValue || 0)}
            color={StatisticDashboardTypeColor.income}
            onClick={viewAccountReceivablesActions.open}
            dashboardModule={StatisticDashboardModule.accountReceivable}
          />
        </Col>

        <Col>
          <StatisticDashboard
            tooltipTitle="Todos os lançamentos do contas a pagar com data de vencimento do mês atual."
            statisticTitle={`A Pagar | ${moment().format('MMM').toUpperCase()}`}
            statisticValue={formatterPrice(data?.payableMonthValue || 0)}
            color={StatisticDashboardTypeColor.expense}
            onClick={viewAccountPayablesActions.open}
            dashboardModule={StatisticDashboardModule.accountPayable}
          />
        </Col>

        <Col>
          <StatisticDashboard
            tooltipTitle="São todos orçamentos que foram abertos no mês atual, que tem a data de abertura dentro do mês atual."
            statisticTitle={
              isMobile
                ? `O.S em aberto | ${moment().format('MMM').toUpperCase()}`
                : `Orçamentos em aberto | ${moment()
                    .format('MMM')
                    .toUpperCase()}`
            }
            statisticValue={String(data?.workOrderOpenMonth || 0)}
            color={StatisticDashboardTypeColor.info}
            onClick={viewWorkOrdersEntryActions.open}
            dashboardModule={StatisticDashboardModule.workOrder}
          />
        </Col>

        <Col>
          <StatisticDashboard
            tooltipTitle="São todos orçamentos que foram autorizados no mês atual, que tem a data de abertura dentro do mês atual"
            statisticTitle={
              isMobile
                ? `O.S autorizados | ${moment().format('MMM').toUpperCase()}`
                : `Orçamentos autorizados | ${moment()
                    .format('MMM')
                    .toUpperCase()}`
            }
            statisticValue={String(data?.workOrderAuthorizedMonth || 0)}
            color={StatisticDashboardTypeColor.authorized}
            onClick={viewWorkOrdersAuthorizationActions.open}
            dashboardModule={StatisticDashboardModule.workOrder}
          />
        </Col>
      </Row>

      <AccountReceivableListModal
        open={viewAccountReceivablesOpen}
        onCancel={viewAccountReceivablesActions.close}
        queryFilter={{
          situation: AccountReceivableSituation.open,
          initialDueDate: moment().startOf('month').format(DateTimeFormat),
          finalDueDate: moment().endOf('month').format(DateTimeFormat),
        }}
      />

      <AccountPayableListModal
        open={viewAccountPayablesOpen}
        onCancel={viewAccountPayablesActions.close}
        queryFilter={{
          situation: InstallmentSituation.open,
          initialDueDate: moment().startOf('month').format(DateTimeFormat),
          finalDueDate: moment().endOf('month').format(DateTimeFormat),
        }}
      />

      <WorkOrdersListModal
        open={viewWorkOrdersEntryOpen}
        onCancel={viewWorkOrdersEntryActions.close}
        queryFilter={{
          entryInitialDate: moment().startOf('month').format(DateTimeFormat),
          entryFinalDate: moment().endOf('month').format(DateTimeFormat),
          status: [WorkOrderComplementStatusNumber.Open],
        }}
      />

      <WorkOrdersListModal
        open={viewWorkOrdersAuthorizationOpen}
        onCancel={viewWorkOrdersAuthorizationActions.close}
        queryFilter={{
          authorizationInitialDate: moment()
            .startOf('month')
            .format(DateTimeFormat),
          authorizationFinalDate: moment()
            .endOf('month')
            .format(DateTimeFormat),
          status: [WorkOrderComplementStatusNumber.Authorized],
        }}
      />
    </SkeletonCustom>
  );
};

export default StatisticManagerView;
